import { MessageCircle } from 'lucide-react'
import type { Lang } from '../../../lib/i18n'
import type { OrderCustomer } from '../../../lib/orders-api'
import { Badge } from '../../../components/ui/badge'
import { Button } from '../../../components/ui/button'
import { Ltr } from '../Ltr'
import { ot } from '../orders-i18n'
import { SectionCard } from './SectionCard'
import { formatOrderId } from '../../../lib/orders-api'

interface Props {
  customer: OrderCustomer
  orderId: string
  shortCode?: string | null
  lang: Lang
}

function waLink(phoneNormalized: string, orderId: string, shortCode?: string | null): string {
  const digits = phoneNormalized.replace(/\D/g, '')
  const text = ot('en', 'whatsappMessage')(formatOrderId(orderId, shortCode))
  return `https://wa.me/${digits}?text=${encodeURIComponent(text)}`
}

export function CustomerSection({ customer, orderId, shortCode, lang }: Props) {
  const orderCount = (customer.wcOrderCount ?? 0) + (customer.mamOrderCount ?? 0) + (customer.mdOrderCount ?? 0)
  const totalSpentRaw =
    (customer.wcTotalSpentRaw ? parseFloat(customer.wcTotalSpentRaw) : 0) +
    (customer.mamTotalSpentRaw ? parseFloat(customer.mamTotalSpentRaw) : 0) +
    (customer.mdTotalSpentRaw ? parseFloat(customer.mdTotalSpentRaw) : 0)
  const isReturning = customer.sourceRecordCount > 1 || orderCount > 0

  return (
    <SectionCard title={ot(lang, 'customer')} lang={lang}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <bdi className="text-base font-semibold text-foreground">{customer.name || '—'}</bdi>
            {customer.isTest && <Badge variant="outline">{ot(lang, 'testOrder')}</Badge>}
          </div>
          {customer.phoneNormalized && (
            <Ltr className="block text-sm text-muted-foreground">{customer.phoneNormalized}</Ltr>
          )}
          <div className="text-sm text-muted-foreground">
            {ot(lang, 'language')}: {customer.languagePreference?.toUpperCase() ?? '—'}
          </div>
          <div className="text-sm">
            {isReturning ? (
              // Single text span, not bare flex children — `inline-flex` on Badge collapses
              // whitespace-only text nodes between adjacent flex items otherwise.
              <Badge variant="sage">
                <span>
                  {ot(lang, 'returning')} · <Ltr>{orderCount || customer.sourceRecordCount}</Ltr> {ot(lang, 'ordersCount')}
                  {totalSpentRaw > 0 && <> · <Ltr>${totalSpentRaw.toFixed(0)}</Ltr> {ot(lang, 'spent')}</>}
                </span>
              </Badge>
            ) : (
              <Badge variant="outline">{ot(lang, 'newCustomer')}</Badge>
            )}
          </div>
        </div>

        {customer.phoneNormalized && (
          <Button asChild variant="sage" size="lg" className="h-14 min-w-[48px] gap-2 px-5">
            <a href={waLink(customer.phoneNormalized, orderId, shortCode)} target="_blank" rel="noreferrer">
              <MessageCircle className="h-5 w-5" />
              {ot(lang, 'whatsapp')}
            </a>
          </Button>
        )}
      </div>
    </SectionCard>
  )
}
